import React, { useState, useRef, useEffect } from 'react';
import { Settings, LogOut, User, ChevronDown, Bell, X, Bot, MessageSquare, Mail, Phone, Check } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthForm } from '../../hooks/useAuthForm';
import { LanguageSelector } from '../LanguageSelector';
import { useNotificationStore, LeadNotification } from '../../stores/notificationStore';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

// Group notifications by chatbot
interface GroupedNotifications {
  [chatbotId: string]: {
    chatbotName: string;
    notifications: LeadNotification[];
    unreadCount: number;
  }
}

export default function AppNavbar() {
  const { user } = useAuth();
  const { signOut } = useAuthForm();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [previousUnreadCount, setPreviousUnreadCount] = useState(0);
  const [latestNotification, setLatestNotification] = useState<LeadNotification | null>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  
  // Notification store
  const { 
    notifications, 
    unreadCount, 
    loading, 
    markAsRead, 
    markAllAsRead,
    markAllAsReadForChatbot,
    setupNotificationListener,
    toggleNotificationsForChatbot,
    preferences
  } = useNotificationStore();

  // Track changes in unread count to show animation
  useEffect(() => {
    if (unreadCount > previousUnreadCount && previousUnreadCount !== 0) {
      // New notification detected - show animation
      setShowAnimation(true);
      
      // Find latest notification to use for toast click action
      if (notifications.length > 0) {
        const sorted = [...notifications].sort((a, b) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        
        const latest = sorted.find(n => !n.read);
        if (latest) {
          setLatestNotification(latest);
        }
      }
      
      // Auto-hide animation after 3 seconds
      const timer = setTimeout(() => {
        setShowAnimation(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
    
    setPreviousUnreadCount(unreadCount);
  }, [unreadCount, previousUnreadCount, notifications]);

  // Group notifications by chatbot
  const groupedNotifications = notifications.reduce<GroupedNotifications>((acc, notification) => {
    const chatbotId = notification.chatbotId || 'unknown';
    
    if (!acc[chatbotId]) {
      acc[chatbotId] = {
        chatbotName: notification.chatbotName || 'Unknown chatbot',
        notifications: [],
        unreadCount: 0
      };
    }
    
    acc[chatbotId].notifications.push(notification);
    
    if (!notification.read) {
      acc[chatbotId].unreadCount++;
    }
    
    return acc;
  }, {});

  // Setup notification listener on component mount
  useEffect(() => {
    const cleanup = setupNotificationListener();
    return () => cleanup();
  }, [setupNotificationListener]);

  // Fermer le menu lorsqu'on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Navigate to lead details
  const handleNotificationClick = (notification: LeadNotification) => {
    markAsRead(notification.id);
    setNotificationsOpen(false);
    
    // First, navigate to the visitors page with all required parameters 
    // This ensures we're looking at the right chatbot and conversation
    if (notification.chatbotId && notification.leadId) {
      // Use the URL with essential query parameters for filtering
      navigate(`/app/visitors?chatbot=${notification.chatbotId}&lead=${notification.leadId}&conversation=${notification.conversationId || ''}`);
    } else {
      // Fallback to all leads if something is missing
      navigate('/app/visitors');
    }
  };

  // Handle "Mark all as read"
  const handleMarkAllAsRead = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    markAllAsRead();
  };

  // Handle "Mark all as read" for a chatbot
  const handleMarkChatbotAsRead = (e: React.MouseEvent, chatbotId: string) => {
    e.preventDefault();
    e.stopPropagation();
    markAllAsReadForChatbot(chatbotId);
  };

  // Toggle notifications for a chatbot
  const handleToggleNotifications = (e: React.MouseEvent, chatbotId: string, enabled: boolean) => {
    e.preventDefault();
    e.stopPropagation();
    toggleNotificationsForChatbot(chatbotId, !enabled);
  };

  // View all leads for a chatbot
  const handleViewChatbotLeads = (chatbotId: string) => {
    setNotificationsOpen(false);
    if (chatbotId === 'unknown') {
      navigate('/app/visitors');
    } else {
      navigate(`/app/visitors?chatbot=${chatbotId}`);
    }
  };

  // Get chatbot notification status
  const getChatbotNotificationStatus = (chatbotId: string): boolean => {
    return preferences[chatbotId] !== false; // Enabled by default unless explicitly disabled
  };

  // Get appropriate notification type icon
  const getLeadTypeIcon = (type: 'email' | 'phone') => {
    return type === 'email' 
      ? <Mail className="w-4 h-4 text-indigo-400" /> 
      : <Phone className="w-4 h-4 text-green-400" />;
  };

  // Handle toast click
  const handleToastClick = () => {
    if (latestNotification) {
      handleNotificationClick(latestNotification);
    } else {
      setNotificationsOpen(true);
    }
    setShowAnimation(false);
  };

  return (
    <nav className="fixed w-full bg-black/10 backdrop-blur-xl z-50 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link 
            to="/" 
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <img 
              src="https://i.imgur.com/3FXFggm.png" 
              alt="Botmaker Agency"
              className="h-10 w-auto"
            />
          </Link>
          
          <div className="flex items-center gap-4">
            <LanguageSelector />

            {/* Notification icon */}
            <div className="relative" ref={notificationsRef}>
              <button 
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                className={`p-2 text-gray-400 hover:text-white transition-colors relative ${
                  showAnimation ? 'animate-pulse' : ''
                }`}
                aria-label={`${unreadCount} unread notifications`}
              >
                <Bell className={`w-5 h-5 ${showAnimation ? 'text-indigo-400' : ''}`} />
                {unreadCount > 0 && (
                  <span className={`absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 bg-red-500 text-white text-[10px] font-semibold rounded-full ${
                    showAnimation ? 'animate-ping-short' : ''
                  }`}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              
              {/* New lead notification toast */}
              {showAnimation && (
                <div 
                  onClick={handleToastClick}
                  className="absolute top-full right-0 mt-2 w-64 bg-indigo-900/90 backdrop-blur text-white rounded-lg shadow-lg p-3 transform transition-transform duration-300 z-50 border border-indigo-700/50 cursor-pointer hover:bg-indigo-800/90 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-indigo-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Nouveau lead détecté!</p>
                      <p className="text-xs text-indigo-200 mt-1">
                        {latestNotification ? (
                          <>
                            <span className="font-semibold">{latestNotification.leadValue}</span>
                            <span> a laissé ses coordonnées.</span>
                          </>
                        ) : (
                          "Un visiteur a laissé ses coordonnées. Cliquez pour voir."
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Notifications dropdown */}
              {notificationsOpen && (
                <div className="absolute right-0 mt-2 w-96 max-h-[80vh] overflow-hidden rounded-xl bg-gray-800/95 backdrop-blur shadow-xl ring-1 ring-gray-700 z-50 flex flex-col">
                  <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                    <h3 className="font-medium text-white flex items-center gap-2">
                      <Bell className="w-4 h-4" />
                      Notifications
                      {unreadCount > 0 && (
                        <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 text-xs font-medium rounded-full">
                          {unreadCount} new
                        </span>
                      )}
                    </h3>
                    {unreadCount > 0 && (
                      <button 
                        onClick={handleMarkAllAsRead}
                        className="text-xs font-medium text-indigo-400 hover:text-indigo-300 transition-colors"
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>
                  
                  {/* Notification content with scrolling */}
                  <div className="overflow-y-auto">
                    {loading ? (
                      <div className="px-4 py-12 text-center text-sm text-gray-400">
                        <div className="inline-block w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin mb-2"></div>
                        <p>Loading notifications...</p>
                      </div>
                    ) : Object.keys(groupedNotifications).length === 0 ? (
                      <div className="px-4 py-12 text-center text-sm text-gray-400">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-700/30 mb-3">
                          <Bell className="w-6 h-6 text-gray-500" />
                        </div>
                        <p>No notifications yet</p>
                      </div>
                    ) : (
                      Object.entries(groupedNotifications).map(([chatbotId, group]) => (
                        <div key={chatbotId} className="border-b border-gray-700/50 last:border-0">
                          {/* Chatbot header */}
                          <div className="px-4 py-3 bg-gray-700/20 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Bot className="w-4 h-4 text-indigo-400" />
                              <span className="font-medium text-white text-sm">
                                {group.chatbotName}
                              </span>
                              {group.unreadCount > 0 && (
                                <span className="px-1.5 py-0.5 bg-indigo-500/20 text-indigo-400 text-[10px] font-medium rounded-full">
                                  {group.unreadCount} new
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {group.unreadCount > 0 && (
                                <button
                                  onClick={(e) => handleMarkChatbotAsRead(e, chatbotId)}
                                  className="text-xs text-gray-400 hover:text-white transition-colors"
                                >
                                  <span className="sr-only">Mark as read</span>
                                  <Check className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={(e) => handleToggleNotifications(e, chatbotId, getChatbotNotificationStatus(chatbotId))}
                                className={`text-xs ${
                                  getChatbotNotificationStatus(chatbotId)
                                    ? 'text-indigo-400 hover:text-indigo-300'
                                    : 'text-gray-500 hover:text-gray-400'
                                } transition-colors`}
                                title={getChatbotNotificationStatus(chatbotId) ? 'Disable notifications' : 'Enable notifications'}
                              >
                                <Bell className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          
                          {/* Chatbot notifications */}
                          <div>
                            {group.notifications.map((notification) => (
                              <button
                                key={notification.id}
                                className={`w-full text-left block px-4 py-3 transition-colors border-b border-gray-700/10 last:border-0 ${
                                  notification.read 
                                    ? 'hover:bg-white/5 text-gray-300' 
                                    : 'hover:bg-indigo-500/10 bg-indigo-500/5 text-white'
                                }`}
                                onClick={() => handleNotificationClick(notification)}
                              >
                                <div className="flex gap-3 items-start">
                                  <div className="mt-0.5">
                                    {getLeadTypeIcon(notification.leadType)}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                      <p className="font-medium truncate">
                                        {notification.leadValue}
                                      </p>
                                      <span className="text-xs text-gray-500 shrink-0 mt-0.5">
                                        {formatDistanceToNow(new Date(notification.createdAt), { 
                                          addSuffix: true,
                                          locale: fr 
                                        })}
                                      </span>
                                    </div>
                                    {notification.firstMessage && (
                                      <p className="text-xs mt-1 line-clamp-2 text-gray-400 flex gap-1 items-start">
                                        <MessageSquare className="w-3 h-3 shrink-0 mt-0.5" />
                                        <span>{notification.firstMessage}</span>
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </button>
                            ))}
                            
                            {/* View all leads for this chatbot */}
                            <div className="px-4 py-2 text-right">
                              <button
                                onClick={() => handleViewChatbotLeads(chatbotId)}
                                className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
                              >
                                View all leads
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  
                  {/* Footer */}
                  {Object.keys(groupedNotifications).length > 0 && (
                    <div className="p-3 border-t border-gray-700 text-center mt-auto">
                      <Link 
                        to="/app/visitors" 
                        className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
                        onClick={() => setNotificationsOpen(false)}
                      >
                        View all collected leads
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Profile menu */}
            <div className="relative" ref={profileMenuRef}>
              <button
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              >
                <div className="w-9 h-9 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400">
                  <User className="w-5 h-5" />
                </div>
                <span className="hidden md:inline max-w-[140px] truncate text-white">
                  {user?.email}
                </span>
                <ChevronDown 
                  className={`w-4 h-4 hidden md:block transition-transform duration-200 ${
                    profileMenuOpen ? 'rotate-180' : ''
                  }`} 
                />
              </button>
              
              {/* Dropdown menu */}
              {profileMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 rounded-xl bg-gray-800/95 backdrop-blur shadow-xl ring-1 ring-gray-700 z-50 overflow-hidden">
                  <div className="py-1">
                    <Link 
                      to="/app/profile" 
                      className="flex items-center px-4 py-3 text-sm text-gray-300 hover:bg-white/5"
                      onClick={() => setProfileMenuOpen(false)}
                    >
                      <User className="w-4 h-4 mr-2 text-indigo-400" />
                      Profile
                    </Link>
                    <Link 
                      to="/app/settings/general" 
                      className="flex items-center px-4 py-3 text-sm text-gray-300 hover:bg-white/5"
                      onClick={() => setProfileMenuOpen(false)}
                    >
                      <Settings className="w-4 h-4 mr-2 text-purple-400" />
                      Settings
                    </Link>
                    <hr className="border-gray-700 my-1" />
                    <button
                      onClick={() => {
                        setProfileMenuOpen(false);
                        signOut();
                      }}
                      className="flex items-center w-full text-left px-4 py-3 text-sm text-red-400 hover:bg-white/5"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}